<template>
  <el-card>
    <template #header>
      <span>SSCMS.com 用户注册</span>
    </template>

    <el-form ref="form" :model="form" label-width="100px" status-icon>
      <el-alert
        style="margin-bottom: 10px"
        :closable="false"
        v-if="errorMessage"
        :title="errorMessage"
        type="error"
        center
        show-icon>
      </el-alert>
      <el-form-item label="用户名" prop="userName" :rules="[{ required: true, message: '请输入用户名' }]">
        <el-input v-model="form.userName"></el-input>
        <small class="tips">用户名是用户的唯一标识，注册后无法更改</small>
      </el-form-item>

      <el-form-item
        label="手机号码"
        prop="mobile"
        :rules="[
          { required: true, message: '请输入手机号码' },
          { validator: validateMobile, message: '请输入有效的手机号码' },
        ]"
      >
        <el-input v-model="form.mobile" placeholder="请输入手机号码"></el-input>
        <small class="tips">手机号码可用于登录、找回密码等功能</small>
      </el-form-item>
      <el-form-item label="短信验证码" prop="code" :rules="[{ required: true, message: '请输入短信验证码' }]">
        <el-input v-model="form.code" autocomplete="off" placeholder="请输入短信验证码">
          <template #append>
            <el-link :underline="false" @click.prevent="btnSendSmsClick">
              {{ countdown > 0 ? countdown + '秒' : '获取验证码' }}
            </el-link>
          </template>
        </el-input>
      </el-form-item>

      <el-form-item label="密码" prop="password" :rules="[{ required: true, message: '请输入密码' }]">
        <el-input v-model="form.password" type="password" name="password" autocomplete="off"></el-input>
      </el-form-item>
      <el-form-item
        label="确认密码"
        prop="confirmPassword"
        :rules="[{ required: true, message: '请输入确认密码' }, { validator: validatePass }]"
      >
        <el-input v-model="form.confirmPassword" type="password" name="confirmPassword" autocomplete="off"></el-input>
      </el-form-item>

      <el-divider></el-divider>
      <div style="height: 10px"></div>
      <el-form-item>
        <div>
          <el-checkbox v-model="isAgreement" style="margin-right: 10px; margin-bottom: 5px">
            阅读并接受<a href="/home/agreement.html" target="_blank">《用户协议》</a>
          </el-checkbox>
        </div>
        <el-button type="primary" :disabled="!isAgreement" @click="btnRegisterClick">注册</el-button>
        <el-link @click.prevent="btnLoginClick" icon="el-icon-user" type="primary" :underline="false" style="margin-left: 10px">
          已有账号，请登录
        </el-link>
      </el-form-item>
    </el-form>
  </el-card>
</template>

<script lang="ts">
import utils from '@/utils';
import api from '@/api/register';
import { IElForm } from '@/types/element';
import { defineComponent } from 'vue';
import { PAGE_TYPE_LOGIN } from '../pageType';

interface IForm {
  userName: string;
  mobile: string;
  code: string;
  password: string;
  confirmPassword: string;
}

interface IState {
  redirect: string;
  isAgreement: boolean;
  errorMessage: string;
  countdown: number;
  form: IForm;
  interval?: number;
  validateMobile: (rule: { message: string }, value: string, callback: (error?: Error) => void) => void;
}

export default defineComponent({
  data() {
    return {
      redirect: this.$route.query.redirect,
      isAgreement: false,
      errorMessage: '',
      countdown: 0,
      form: {
        userName: '',
        mobile: '',
        code: '',
        password: '',
        confirmPassword: '',
      },
      interval: 0,
      validateMobile: utils.validateMobile,
    } as IState;
  },
  methods: {
    apiSendSms() {
      api.sendSms(
        {
          mobile: this.form.mobile,
        },
        (res) => {
          if (!res.value) return;
          utils.notifySuccess('验证码发送成功，10分钟内有效');
          this.countdown = 60;
          this.interval = window.setInterval(() => {
            this.countdown -= 1;
            if (this.countdown <= 0) {
              clearInterval(this.interval);
            }
          }, 1000);
        },
      );
    },

    apiSubmit() {
      api.submit(
        {
          userName: this.form.userName,
          password: this.form.password,
          mobile: this.form.mobile,
          code: this.form.code,
        },
        (res) => {
          utils.loading(false);
          if (res.value) {
            utils.alertSuccess(
              {
                title: '恭喜，账号注册成功',
                button: '进入登录页',
              },
              () => {
                this.btnLoginClick();
              },
            );
          }
        },
        (errorMessage: string) => {
          this.errorMessage = errorMessage;
          utils.loading(false);
        },
      );
    },

    isMobileValid(value: string) {
      return /^1[3-9][0-9]\d{8}$/.test(value);
    },

    btnSendSmsClick() {
      if (this.countdown > 0) return;
      if (!this.form.mobile) {
        this.errorMessage = '手机号码不能为空';
        return;
      }
      if (!this.isMobileValid(this.form.mobile)) {
        this.errorMessage = '请输入有效的手机号码';
        return;
      }

      this.apiSendSms();
    },

    btnRegisterClick() {
      this.errorMessage = '';
      (this.$refs.form as IElForm).validate((valid) => {
        if (valid) {
          this.apiSubmit();
        }
      });
    },

    btnLoginClick() {
      this.$emit('redirect', PAGE_TYPE_LOGIN);
    },

    validatePass(rule: string, value: string, callback: (error?: Error) => void) {
      if (value === '') {
        callback(new Error('请再次输入密码'));
      } else if (value !== this.form.password) {
        callback(new Error('两次输入密码不一致!'));
      } else {
        callback();
      }
    },
  },
  mounted() {
    utils.loading(false);
  },
});
</script>

<style>
.wrapper-page {
  margin: 4% auto;
  max-width: 460px;
}
.el-checkbox__label a {
  color: #409eff;
  text-decoration: none;
}
</style>
